import type { HTMLAttributes } from "react";
import { cx } from "../cx";

const span = (cls: string) =>
  function Text({ className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
    return <span className={cx(cls, className)} {...rest} />;
  };
export const Hint = span("sk-hint");
export const Mono = span("sk-mono");
export const Kbd = span("sk-kbd");
export const LabelText = span("sk-label-text");
/** Solo para el lector (`sk-vh`): el «fichas ·» que une un número a su desglose en una celda. */
export const Vh = span("sk-vh");
/** Overline de sección/eyebrow: bloque (el prototipo usa <div>). `as`: un
    párrafo (sobretítulo) o un h3 cuando titula una sección de un diálogo. */
export function Overline({ as: Tag = "div", className, ...rest }: { as?: "div" | "p" | "h3" | undefined } & HTMLAttributes<HTMLElement>) {
  return <Tag className={cx("sk-overline", className)} {...rest} />;
}
/** Texto atenuado; `block` = párrafo (como el <p class="sk-fainted"> del prototipo). */
export function Fainted({ block, className, ...rest }: { block?: boolean | undefined } & HTMLAttributes<HTMLElement>) {
  return block ? <p className={cx("sk-fainted", className)} {...rest} /> : <span className={cx("sk-fainted", className)} {...rest} />;
}
export function Whisper({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cx("sk-whisper", className)} {...rest} />;
}
/** Sub-línea standalone (mono, atenuada) — a diferencia del `sub` inline de
    CellTitle/MemList, no va pegada a un título: es su propio bloque. */
export function Sub({ inline, className, ...rest }: { inline?: boolean | undefined } & HTMLAttributes<HTMLElement>) {
  // `inline`: dentro de contenido de frase (un botón de la lista, una celda
  // tras un título suelto) un <div> no vale; el sistema pinta igual el span
  return inline === true ? <span className={cx("sk-sub", className)} {...rest} /> : <div className={cx("sk-sub", className)} {...rest} />;
}
/** Ayuda o glosa del porqué. `inline` = dentro de una fila de actos (span). */
/** `note` = nota que CIERRA un grupo de filas de ajustes: aire arriba y abajo
    para que el borde de la fila siguiente no se pegue al texto (sk-setrow-note). */
export function Help({ inline, note, className, ...rest }: { inline?: boolean | undefined; note?: boolean | undefined } & HTMLAttributes<HTMLElement>) {
  const cls = cx("sk-help", note === true && "sk-setrow-note", className);
  return inline === true ? <span className={cls} {...rest} /> : <p className={cls} {...rest} />;
}
/** Titulares del sistema. `anchor` = foco programático (tabindex=-1) para que
    el lector anuncie dónde ha caído al conmutar de estado. */
export function H1({ anchor, className, ...rest }: { anchor?: boolean | undefined } & HTMLAttributes<HTMLHeadingElement>) {
  return <h1 className={cx("sk-h1", className)} tabIndex={anchor === true ? -1 : undefined} {...rest} />;
}
/** `look="h3"`: un h2 por jerarquía con la talla de h3 (`h2.sk-h3`), como el
    título del resultado del simulador, que cuelga del h1 de la página. */
export function H2({ look, className, ...rest }: { look?: "h3" | undefined } & HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cx(look === "h3" ? "sk-h3" : "sk-h2", className)} {...rest} />;
}
export function H3({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cx("sk-h3", className)} {...rest} />;
}
/** Prosa larga (serif). `muted` = la explicación de una tarjeta escenario. */
export function ProseP({ muted, className, ...rest }: { muted?: boolean | undefined } & HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cx("sk-prose", muted && "sk-muted", className)} {...rest} />;
}
/** Envoltorio inerte de tooltip del sistema (data-sk-tip); sin estilo propio — lo lleva el hijo. */
export function Tip({ tip, pos, ...rest }: { tip: string; pos?: "bottom" | "right" | "left" | undefined } & HTMLAttributes<HTMLSpanElement>) {
  return <span data-sk-tip={tip} data-sk-tip-pos={pos} {...rest} />;
}
/** Marca: SKE<i>I</i>N (la i en oro la pone el sistema). `hero` = la marca de
    la puerta del Despacho, SOBRE la tarjeta escenario (acceso.html). */
export function Brand({ wordmark, hero, size, children, className, style }: { wordmark?: boolean | undefined; hero?: boolean | undefined; size?: string | undefined; children?: React.ReactNode; className?: string | undefined; style?: React.CSSProperties | undefined }) {
  return (
    <div className={cx("sk-brand", wordmark && "sk-brand--wordmark", hero && "sk-hero-brand", className)} style={{ ...(size ? { fontSize: size } : {}), ...style }}>
      {children ?? <>SKE<i>I</i>N</>}
    </div>
  );
}
/**
 * Región viva (`role=status`) MONTADA SIEMPRE: lo que aparece dentro se
 * anuncia (una región que nace con contenido no se anuncia). Vacía, queda
 * oculta a la vista (`sk-vh`) y no ocupa sitio en la retícula; con aviso,
 * se ve. `ref` + `tabIndex=-1`: la pantalla le lleva el foco al fallar (los
 * avisos de una firma, ficha.html).
 */
export function LiveRegion({ children, ref }: { children?: React.ReactNode; ref?: React.Ref<HTMLDivElement> | undefined }) {
  const vacia = children === undefined || children === null || children === false;
  return <div role="status" ref={ref} tabIndex={-1} className={vacia ? "sk-vh" : undefined}>{children}</div>;
}
